import moment from 'moment';

// The one-row CSV export for a single message, copied from
// generateCsvDataForSingleMsg in Component/Analytics/Components/ExportBtn.js,
// so the columns and file name match the current Analytics page.
// - message: the full message (getMsgBy_Id), with topicIds and selectedGroupIds
// - emailEvents, textEvents: from getMessageEventInfo
// - groups, topics: { [id]: name } for the organization
// Returns { messages, filename } for updateProgressMeterResults.
export const getSingleMessageExport = ({
  organization,
  message,
  emailEvents,
  textEvents,
  groups,
  topics,
}) => {
  const selectedTopics = [];
  const selectedGroups = [];
  for (const topicId of message.topicIds) {
    selectedTopics.push(topics[topicId]);
  }
  for (const groupId of message.selectedGroupIds) {
    selectedGroups.push(groups[groupId]);
  }

  const getRate = (numerator, denominator) => {
    return numerator > 0 && denominator > 0
      ? `${((numerator / denominator) * 100).toFixed(1)}%`
      : '0%';
  };

  const messageDetails = [
    {
      'Message ID': message.id,
      'Message URL': `${process.env.REACT_APP_webUrl}/message?id=${message.id}`,
      'Message Subject': message.messageSubject,
      Date: moment.unix(message.sendTime).format('MM-DD-YYYY hh:mm A'),
      Topics: selectedTopics,
      Groups: selectedGroups,
      '# Email Deliveries': emailEvents.delivered,
      'Email Delivery Rate': getRate(emailEvents.delivered, emailEvents.sent),
      '# Opens': emailEvents.uniqueopen,
      'Open Rate': getRate(emailEvents.uniqueopen, emailEvents.delivered),
      '# Clickthroughs': emailEvents.uniqueclick,
      'Clickthrough Rate': getRate(
        emailEvents.uniqueclick,
        emailEvents.uniqueopen
      ),
      '# Email Permanent Bounces': emailEvents.permanentBounce,
      'Email Permanent Bounce Rate': getRate(
        emailEvents.permanentBounce,
        emailEvents.sent
      ),
      '# Email Temporary Bounces': emailEvents.temporaryBounce,
      'Email Temporary Bounce Rate': getRate(
        emailEvents.temporaryBounce,
        emailEvents.sent
      ),
      '# Email Drops': emailEvents.dropped,
      'Email Drop Rate': getRate(emailEvents.dropped, emailEvents.sent),
      '# Spam Reports': emailEvents.spamReport,
      'Spam Report Rate': getRate(emailEvents.spamReport, emailEvents.sent),
      '# Text Deliveries': textEvents.delivered,
      'Text Delivery Rate': getRate(textEvents.delivered, textEvents.sent),
      '# Text Bounces': textEvents.bounced,
      'Text Bounce Rate': getRate(textEvents.bounced, textEvents.sent),
    },
  ];

  return {
    messages: messageDetails,
    filename: `${organization.acronym} Analytics Export, '${message.messageSubject}'.csv`,
  };
};

// A CSV file for rows of { [column]: value }, with the first row's keys as
// the header. Every value is quoted, with its quotes doubled.
export const toCsv = rows => {
  if (rows.length === 0) return '';
  const columns = Object.keys(rows[0]);
  const quote = value => `"${String(value ?? '').replace(/"/g, '""')}"`;
  return [columns, ...rows.map(row => columns.map(column => row[column]))]
    .map(values => values.map(quote).join(','))
    .join('\n');
};
